// Analytics tracking utility for frontend
// Tracks user events and sends them to the backend

import { getParsedUser } from './authStorage';
import { getSessionId } from './sessionTracker';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || '';

// Get user ID if logged in
const getUserId = () => {
  const user = getParsedUser();
  return user ? user._id || user.id || null : null;
};

// Track analytics event
export const trackEvent = async (eventType, data = {}) => {
  try {
    if (!API_BASE_URL) {
      console.warn('API_BASE_URL not set, analytics tracking disabled');
      return;
    }

    const sessionId = getSessionId();
    const userId = getUserId();

    const eventData = {
      eventType,
      sessionId,
      userId: userId || undefined,
      ...data,
      timestamp: new Date().toISOString()
    };

    // Send to backend (non-blocking)
    fetch(`${API_BASE_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData)
    }).catch(error => {
      // Silently fail - analytics should not block user experience
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics tracking error:', error);
      }
    });
  } catch (error) {
    // Silently fail - analytics should not block user experience
    if (process.env.NODE_ENV === 'development') {
      console.error('Analytics tracking error:', error);
    }
  }
};

// Track page view
export const trackPageView = (pathname, metadata = {}) => {
  trackEvent('view', {
    page: pathname,
    ...metadata
  });
};

// Track search
export const trackSearch = (searchQuery, metadata = {}) => {
  trackEvent('search', {
    searchQuery,
    ...metadata
  });
};

// Track product view
export const trackProductView = (productId, categoryId = null, metadata = {}) => {
  trackEvent('view', {
    productId,
    categoryId,
    ...metadata
  });
};

// Track add to cart
export const trackAddToCart = (productId, categoryId = null, metadata = {}) => {
  trackEvent('add_to_cart', {
    productId,
    categoryId,
    ...metadata
  });
};

// Track purchase
export const trackPurchase = (orderId, products = [], totalAmount = 0, metadata = {}) => {
  trackEvent('purchase', {
    orderId,
    products,
    totalAmount,
    ...metadata
  });
};

function storeMetaFromProduct(product = {}) {
  return {
    storeId: product.storeId || product.vendorId || product.railwayStoreId || '',
    storeName: product.storeName || product.vendor || '',
  };
}

export const trackCheckAvailability = (product, { source = 'detail', ...extra } = {}) => {
  if (!product) return;
  trackEvent('check_availability', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      source,
      ...extra,
    },
  });
};

export const trackAddToCompareEvent = (products, { source = 'card', ...extra } = {}) => {
  const list = Array.isArray(products) ? products : [products];
  list.filter(Boolean).forEach((product) => {
    trackEvent('add_to_compare', {
      productId: product._id || product.id,
      categoryId: product.category,
      metadata: {
        ...storeMetaFromProduct(product),
        source,
        ...extra,
      },
    });
  });
};

export const trackSaveDeal = (product, metadata = {}) => {
  if (!product) return;
  trackEvent('save_deal', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      ...metadata,
    },
  });
};

export const trackBannerClick = ({ bannerId, slideId, title, link } = {}) => {
  trackEvent('banner_click', {
    metadata: {
      bannerId: bannerId || '',
      slideId: slideId || '',
      title: title || '',
      link: link || '',
    },
  });
};

export const trackCategoryClick = (category, metadata = {}) => {
  if (!category) return;
  const categoryId = category._id || category.id;
  trackEvent('category_click', {
    categoryId,
    metadata: {
      categoryName: category.name || '',
      ...metadata,
    },
  });
};

export const trackPriceMatchPanelView = (product, priceMatch, metadata = {}) => {
  if (!product || !priceMatch) return;
  trackEvent('price_match_panel_view', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      competitorStoreName: priceMatch.competitorStoreName || '',
      savingsAmount: priceMatch.savingsAmount || 0,
      matchTier: priceMatch.matchTier || '',
      ...metadata,
    },
  });
};

export const trackPriceMatchBadgeView = (product, priceMatch, metadata = {}) => {
  if (!product || !priceMatch) return;
  trackEvent('price_match_badge_view', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      savingsAmount: priceMatch.savingsAmount || 0,
      source: metadata.source || 'card',
      ...metadata,
    },
  });
};

export const trackPriceMatchClaimClick = (product, priceMatch, metadata = {}) => {
  if (!product || !priceMatch) return;
  trackEvent('price_match_claim_click', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      claimUrl: metadata.claimUrl || '',
      competitorStoreName: priceMatch.competitorStoreName || '',
      savingsAmount: priceMatch.savingsAmount || 0,
      ...metadata,
    },
  });
};

export const trackPriceMatchCopyProof = (product, priceMatch, metadata = {}) => {
  if (!product || !priceMatch) return;
  trackEvent('price_match_copy_proof', {
    productId: product._id || product.id,
    categoryId: product.category,
    metadata: {
      ...storeMetaFromProduct(product),
      competitorStoreName: priceMatch.competitorStoreName || '',
      ...metadata,
    },
  });
};

export const trackPriceMatchFilterToggle = (active, count, metadata = {}) => {
  trackEvent('price_match_filter_toggle', {
    metadata: {
      active: active === true,
      count: Number.isFinite(Number(count)) ? Number(count) : 0,
      ...metadata,
    },
  });
};

export const trackPriceMatchLandingView = (metadata = {}) => {
  trackEvent('price_match_landing_view', {
    metadata: {
      count: Number.isFinite(Number(metadata.count)) ? Number(metadata.count) : 0,
      enabled: metadata.enabled !== false,
      category: metadata.category || 'all',
      sort: metadata.sort || 'savings',
      ...metadata,
    },
  });
};

export const trackSearchNoResults = (searchQuery, resultCount = 0, metadata = {}) => {
  const query = String(searchQuery || '').trim();
  if (!query) return;
  trackEvent('search_no_results', {
    searchQuery: query,
    metadata: {
      resultCount: Number(resultCount) || 0,
      ...metadata,
    },
  });
};
